import { datadogRum } from '@datadog/browser-rum'
import * as codeSystems from '@seen/core/codeSystems'
import { toDateString } from '@seen/core/dates'
import { ExtendedImmunization } from '@seen/core/models/immunizations/ExtendedImmunization'
import { gtinToNDC, parseGS1Barcode } from '@seen/core/vaccine-barcode-parser'
import {
  lookupVaccineByGTIN,
  lookupVaccineByNDC,
} from '@seen/core/vaccine-ndc-lookup'
import { Button } from '@seen/ui'
import { IconLoader2 } from '@tabler/icons-react'
import { compact } from 'lodash-es'
import { type JSX, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import { z } from 'zod'
import { Combobox } from '#src/components/core/combobox/Combobox.tsx'
import { Form } from '#src/components/core/form/Form.tsx'
import { FormField } from '#src/components/core/form/FormField.tsx'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { DatePicker } from '#src/components/input/DatePicker.tsx'
import { PractitionerNameplate } from '#src/components/nameplates/PractitionerNameplate.tsx'
import { PractitionerOption } from '#src/components/options/PractitionerOption.tsx'
import { ValueSetAutocomplete } from '#src/components/ValueSetAutocomplete/ValueSetAutocomplete.tsx'
import { useCurrentParticipant } from '#src/contexts/current-participant/useCurrentParticipant.ts'
import { useCreateMutation } from '#src/hooks/useCreateMutation.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useForm } from '#src/hooks/useForm.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useParticipantPcp } from '#src/hooks/useParticipantPcp.ts'
import { usePractitionersInternal } from '#src/hooks/usePractitionersInternal.ts'

const immunizationFormSchema = z
  .object({
    wasRefused: z.boolean(),
    refusalReason: z.string().nullable(),
    barcode: z.string().nullable(),
    vaccine: z
      .object({
        code: z.string(),
        display: z.string(),
      })
      .refine((vaccine) => vaccine.code.length > 0, 'Vaccine is required'),
    administeredDate: z.string().date(),
    site: z.string().nullable(),
    route: z.string().nullable(),
    lotNumber: z.string().nullable(),
    manufacturer: z
      .object({
        code: z.string(),
        display: z.string(),
      })
      .nullable(),
    doseQuantity: z.string().nullable(),
    doseUnit: z.string().nullable(),
    reason: z.string().nullable(),
    notes: z.string().nullable(),
    orderingPractitioner: z.string().nullable(),
    administeringPractitioner: z.string().nullable(),
  })
  .refine(
    (data) => {
      if (data.wasRefused && !data.refusalReason) {
        return false
      }
      return true
    },
    {
      message: 'Refusal reason is required when immunization is refused',
      path: ['refusalReason'],
    }
  )

export type ImmunizationFormValues = z.infer<typeof immunizationFormSchema>

export interface ImmunizationFormProps {
  onSubmit: (immunization: ExtendedImmunization) => void
  onCancel: () => void
  defaultValues?: Partial<ImmunizationFormValues>
}

interface RouteMapLookup {
  [key: string]: string
}

export function ImmunizationForm({
  onSubmit,
  onCancel,
  defaultValues,
}: ImmunizationFormProps): JSX.Element {
  const [barcodeStatus, setBarcodeStatus] = useState<
    'idle' | 'success' | 'error'
  >('idle')
  const [barcodeMessage, setBarcodeMessage] = useState<string>('')
  const defaultAdministeredDate = useMemo(() => toDateString(new Date()), [])
  const participant = useCurrentParticipant()
  const isReadOnly = useIsReadOnly()
  const currentPractitioner = useCurrentPractitioner()
  const practitioners = usePractitionersInternal()
  const { pcp: participantPcp, isPending: isParticipantPcpPending } =
    useParticipantPcp(participant.reference)

  const createImmunization = useCreateMutation<ExtendedImmunization>({
    disableDefaultErrorToast: true,
    mutation: {
      onSuccess: (immunization) => {
        onSubmit(immunization)
      },
      onError: (cause: unknown) => {
        toast.error(cause instanceof Error ? cause.message : 'Unknown error')
      },
    },
  })

  const form = useForm({
    schema: immunizationFormSchema,
    defaultValues: {
      wasRefused: false,
      refusalReason: null,
      barcode: null,
      vaccine: {
        code: '',
        display: '',
      },
      administeredDate: defaultAdministeredDate,
      site: null,
      route: null,
      lotNumber: null,
      manufacturer: null,
      doseQuantity: null,
      doseUnit: null,
      reason: null,
      notes: null,
      // Ordering provider defaults to the participant's PCP (seeded async once
      // the IDT loads, below) — not the current user, who is often the MA giving
      // the shot. Left blank until the PCP resolves; blank is the safe fallback
      // when the participant has no PCP so the user must choose deliberately.
      orderingPractitioner: null,
      administeringPractitioner: currentPractitioner.reference.reference,
      ...defaultValues,
    },
  })

  // Seed the ordering provider with the participant's PCP once the IDT resolves
  // (react-hook-form recommends mutating values from an effect, not in render).
  // Seeds at most once and skips when a caller passed an explicit override or
  // the user already picked someone, so it never clobbers a deliberate choice.
  const didSeedOrderingProvider = useRef(false)
  useEffect(() => {
    if (didSeedOrderingProvider.current) {
      return
    }
    if (defaultValues?.orderingPractitioner !== undefined) {
      return
    }
    // Reference.reference is optional in FHIR; if the PCP member has no usable
    // reference string, treat it as "no PCP" and leave the field blank rather
    // than seeding an empty value.
    const pcpReference = participantPcp?.reference
    if (!pcpReference || form.getValues('orderingPractitioner')) {
      return
    }
    form.setValue('orderingPractitioner', pcpReference)
    didSeedOrderingProvider.current = true
  }, [participantPcp?.reference, defaultValues?.orderingPractitioner, form])

  const wasRefused = form.watch('wasRefused')
  const hasVaccine = form.watch('vaccine.code').length > 0

  const handleBarcodeInput = async (barcodeValue: string): Promise<void> => {
    if (!barcodeValue || barcodeValue.trim().length === 0) {
      setBarcodeStatus('idle')
      setBarcodeMessage('')
      return
    }

    try {
      // Parse GS1 barcode
      const parsed = parseGS1Barcode(barcodeValue)

      if (!parsed.gtin) {
        setBarcodeStatus('error')
        setBarcodeMessage('Invalid barcode format. Could not find GTIN.')
        return
      }

      let vaccineInfo = await lookupVaccineByGTIN(parsed.gtin)

      if (!vaccineInfo) {
        const ndcResult = gtinToNDC(parsed.gtin)
        if (ndcResult) {
          vaccineInfo = await lookupVaccineByNDC(ndcResult.ndc11)
        }
      }

      // A parsed GTIN means a real GS1 scanner read — stray keystrokes into
      // the input never decode to a valid GTIN, so this counts genuine scans
      // only. Lets us judge whether the barcode field is worth keeping.
      // Datadog query: `@action.name:"immunization.barcode_scanned"`
      datadogRum.addAction('immunization.barcode_scanned', {
        matched: vaccineInfo !== null,
      })

      if (!vaccineInfo) {
        setBarcodeStatus('error')
        setBarcodeMessage(
          'Vaccine not found in CDC database. Please enter manually.'
        )
        return
      }

      form.setValue('vaccine', {
        code: vaccineInfo.cvxCode,
        display: vaccineInfo.cvxDisplay,
      })

      if (parsed.lotNumber) {
        form.setValue('lotNumber', parsed.lotNumber)
      }

      if (vaccineInfo.manufacturerCode && vaccineInfo.manufacturer) {
        form.setValue('manufacturer', {
          code: vaccineInfo.manufacturerCode,
          display: vaccineInfo.manufacturer,
        })
      }

      if (vaccineInfo.route) {
        const routeMap: RouteMapLookup = {
          INTRAMUSCULAR: 'Intramuscular',
          ORAL: 'Oral',
          INTRANASAL: 'Intranasal',
          SUBCUTANEOUS: 'Subcutaneous',
          INTRADERMAL: 'Intradermal',
        }
        const normalizedRoute = routeMap[vaccineInfo.route] || vaccineInfo.route
        form.setValue('route', normalizedRoute)
      }

      // Note: expiration date from barcode is typically for the vial itself,
      // not the administered date, so we don't auto-populate administeredDate

      setBarcodeStatus('success')
      setBarcodeMessage(
        `Populated: ${vaccineInfo.vaccineName} (CVX ${vaccineInfo.cvxCode})`
      )

      // Clear barcode input after successful scan
      form.setValue('barcode', null)
    } catch (error) {
      setBarcodeStatus('error')
      setBarcodeMessage(
        `Error processing barcode: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
    }
  }

  const handleFormSubmit = (values: ImmunizationFormValues): void => {
    const immunization = new ExtendedImmunization({
      status: values.wasRefused ? 'not-done' : 'completed',
      statusReason: values.wasRefused
        ? {
            text: values.refusalReason ?? undefined,
          }
        : undefined,
      vaccineCode: {
        coding: [
          {
            system: codeSystems.CVX_SYSTEM,
            code: values.vaccine.code,
            display: values.vaccine.display,
          },
        ],
        text: values.vaccine.display,
      },
      patient: participant.reference,
      occurrenceDateTime: values.administeredDate,
      site: values.site
        ? {
            text: values.site,
          }
        : undefined,
      route: values.route
        ? {
            text: values.route,
          }
        : undefined,
      lotNumber: values.lotNumber ?? undefined,
      manufacturer: values.manufacturer
        ? {
            display: values.manufacturer.display,
          }
        : undefined,
      doseQuantity: values.doseQuantity
        ? {
            value: parseFloat(values.doseQuantity),
            unit: values.doseUnit ?? 'ml',
            system: codeSystems.UCUM_SYSTEM,
            code: values.doseUnit ?? 'ml',
          }
        : undefined,
      performer: compact([
        values.orderingPractitioner && {
          function: codeSystems.immunizationPerformerFunctionOrderingProvider,
          actor: { reference: values.orderingPractitioner },
        },
        !values.wasRefused &&
          values.administeringPractitioner && {
            function:
              codeSystems.immunizationPerformerFunctionAdministeringProvider,
            actor: { reference: values.administeringPractitioner },
          },
      ]),
      reasonCode: values.reason
        ? [
            {
              text: values.reason,
            },
          ]
        : undefined,
      note: values.notes
        ? [
            {
              text: values.notes,
            },
          ]
        : undefined,
    })

    if (!values.wasRefused) {
      immunization.barcode = values.barcode ?? undefined
    }

    createImmunization.mutate(immunization)
  }

  return (
    <Form
      onSubmit={form.handleSubmit(handleFormSubmit)}
      className="flex flex-col gap-4"
    >
      <FormField
        control={form.control}
        name="barcode"
        render={({ field }) => (
          <div className="flex flex-col gap-2 rounded-md border border-border bg-muted p-4">
            <label className="flex flex-col gap-1">
              <span className="text-sm font-medium">
                Vaccine Barcode (Optional)
              </span>
              <input
                {...field}
                value={field.value ?? ''}
                onChange={(e) => {
                  field.onChange(e.target.value)
                  void handleBarcodeInput(e.target.value)
                }}
                className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                placeholder="Focus here and scan GS1 DataMatrix with scanner"
                autoComplete="off"
              />
            </label>
            {barcodeStatus !== 'idle' && (
              <div
                className={`text-sm ${
                  barcodeStatus === 'success'
                    ? 'text-green-600'
                    : 'text-red-600'
                }`}
              >
                {barcodeStatus === 'success' ? '✓ ' : '✗ '}
                {barcodeMessage}
              </div>
            )}
          </div>
        )}
      />

      <FormField
        control={form.control}
        name="wasRefused"
        render={({ field }) => (
          <label className="flex w-fit cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={field.value}
              onChange={(e) => field.onChange(e.target.checked)}
              className="size-4 rounded border-input bg-background"
            />
            <span className="text-sm font-medium">
              Patient refused immunization
            </span>
          </label>
        )}
      />

      {wasRefused && (
        <FormField
          control={form.control}
          name="refusalReason"
          render={({ field }) => (
            <label className="flex flex-col gap-1">
              <span className="text-sm font-medium">
                Refusal Reason <span className="text-orange-500">*</span>
              </span>
              <input
                {...field}
                value={field.value ?? ''}
                onChange={(e) => field.onChange(e.target.value || null)}
                className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                placeholder="e.g., Parental decision, Religious exemption, etc."
              />
            </label>
          )}
        />
      )}

      <FormField
        control={form.control}
        name="vaccine"
        render={({ field: { value, onChange, ...fieldRest } }) => {
          const { code, display } = value

          return (
            <label className="flex flex-col gap-1">
              <span className="text-sm font-medium">
                Vaccine <span className="text-orange-500">*</span>
              </span>
              <ValueSetAutocomplete
                binding="http://hl7.org/fhir/sid/cvx/vs"
                values={
                  value
                    ? [
                        {
                          code,
                          display,
                          system: 'http://hl7.org/fhir/sid/cvx',
                        },
                      ]
                    : []
                }
                onSelect={(value) => {
                  onChange(value ?? { code: '', display: '' })
                }}
                {...fieldRest}
              >
                <Button variant="outline" className="w-full justify-between">
                  {code ? `${code} - ${display}` : 'Search vaccine…'}
                </Button>
              </ValueSetAutocomplete>
            </label>
          )
        }}
      />

      <FormField
        control={form.control}
        name="administeredDate"
        render={({ field }) => (
          <label className="flex w-fit flex-col items-start gap-1">
            <span className="text-sm font-medium">
              Date Administered <span className="text-orange-500">*</span>
            </span>
            <DatePicker
              value={field.value}
              onChange={(value) => field.onChange(value ?? '')}
            />
          </label>
        )}
      />

      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="orderingPractitioner"
          render={({ field }) => {
            const selectedPractitioner = practitioners.find(
              (p) => p.reference.reference === field.value
            )

            // While the PCP lookup is still resolving and nothing is selected
            // yet, show a loading state so the empty field doesn't read as
            // "no ordering provider" before the PCP default lands.
            const isResolvingDefault =
              isParticipantPcpPending &&
              defaultValues?.orderingPractitioner === undefined &&
              !field.value

            return (
              <label className="flex flex-col gap-1">
                <span className="text-sm font-medium">
                  Ordering Practitioner
                </span>
                <Combobox
                  onSelect={(value) => field.onChange(value)}
                  options={practitioners.map((practitioner) => ({
                    value: practitioner.reference.reference,
                    label: (
                      <PractitionerOption
                        practitioner={practitioner.reference}
                        isSelected={
                          practitioner.reference.reference === field.value
                        }
                      />
                    ),
                    keywords: practitioner.searchKeywords,
                  }))}
                >
                  <Button variant="outline" className="w-full justify-between">
                    {isResolvingDefault ? (
                      <span className="flex items-center gap-2 text-muted-foreground">
                        <IconLoader2 className="size-4 animate-spin" />
                        Loading ordering provider…
                      </span>
                    ) : selectedPractitioner ? (
                      <PractitionerNameplate
                        practitioner={selectedPractitioner.reference}
                        size={3.5}
                      />
                    ) : (
                      'Select practitioner...'
                    )}
                  </Button>
                </Combobox>
              </label>
            )
          }}
        />

        {!wasRefused && (
          <FormField
            control={form.control}
            name="administeringPractitioner"
            render={({ field }) => {
              const selectedPractitioner = practitioners.find(
                (p) => p.reference.reference === field.value
              )

              return (
                <label className="flex flex-col gap-1">
                  <span className="text-sm font-medium">
                    Administering Practitioner
                  </span>
                  <Combobox
                    onSelect={(value) => field.onChange(value)}
                    options={practitioners.map((practitioner) => ({
                      value: practitioner.reference.reference,
                      label: (
                        <PractitionerOption
                          practitioner={practitioner.reference}
                          isSelected={
                            practitioner.reference.reference === field.value
                          }
                        />
                      ),
                      keywords: practitioner.searchKeywords,
                    }))}
                  >
                    <Button
                      variant="outline"
                      className="w-full justify-between"
                    >
                      {selectedPractitioner ? (
                        <PractitionerNameplate
                          practitioner={selectedPractitioner.reference}
                          size={3.5}
                        />
                      ) : (
                        'Select practitioner...'
                      )}
                    </Button>
                  </Combobox>
                </label>
              )
            }}
          />
        )}
      </div>

      {!wasRefused && (
        <>
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="site"
              render={({ field }) => (
                <label className="flex flex-col gap-1">
                  <span className="text-sm font-medium">Site</span>
                  <Combobox
                    onSelect={(value) => field.onChange(value)}
                    options={[
                      {
                        value: 'Left arm',
                        label: 'Left arm',
                        keywords: ['left', 'arm'],
                      },
                      {
                        value: 'Right arm',
                        label: 'Right arm',
                        keywords: ['right', 'arm'],
                      },
                      {
                        value: 'Left thigh',
                        label: 'Left thigh',
                        keywords: ['left', 'thigh'],
                      },
                      {
                        value: 'Right thigh',
                        label: 'Right thigh',
                        keywords: ['right', 'thigh'],
                      },
                      {
                        value: 'Left deltoid',
                        label: 'Left deltoid',
                        keywords: ['left', 'deltoid'],
                      },
                      {
                        value: 'Right deltoid',
                        label: 'Right deltoid',
                        keywords: ['right', 'deltoid'],
                      },
                    ]}
                  >
                    <Button
                      variant="outline"
                      className="w-full justify-between"
                    >
                      {field.value ?? 'e.g., Left arm'}
                    </Button>
                  </Combobox>
                </label>
              )}
            />

            <FormField
              control={form.control}
              name="route"
              render={({ field }) => (
                <label className="flex flex-col gap-1">
                  <span className="text-sm font-medium">Route</span>
                  <Combobox
                    onSelect={(value) => field.onChange(value)}
                    options={[
                      {
                        value: 'Intramuscular',
                        label: 'Intramuscular',
                        keywords: ['intramuscular', 'im'],
                      },
                      {
                        value: 'Subcutaneous',
                        label: 'Subcutaneous',
                        keywords: ['subcutaneous', 'subq'],
                      },
                      {
                        value: 'Intranasal',
                        label: 'Intranasal',
                        keywords: ['intranasal', 'nasal'],
                      },
                      {
                        value: 'Oral',
                        label: 'Oral',
                        keywords: ['oral', 'po'],
                      },
                      {
                        value: 'Intradermal',
                        label: 'Intradermal',
                        keywords: ['intradermal', 'id'],
                      },
                    ]}
                  >
                    <Button
                      variant="outline"
                      className="w-full justify-between"
                    >
                      {field.value ?? 'e.g., Intramuscular'}
                    </Button>
                  </Combobox>
                </label>
              )}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="lotNumber"
              render={({ field }) => (
                <label className="flex flex-col gap-1">
                  <span className="text-sm font-medium">Lot Number</span>
                  <input
                    {...field}
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value || null)}
                    className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                    placeholder="e.g., ABC123"
                  />
                </label>
              )}
            />

            <FormField
              control={form.control}
              name="manufacturer"
              render={({ field: { value, onChange, ...fieldRest } }) => {
                const { code, display } = value ?? { code: '', display: '' }

                return (
                  <label className="flex flex-col gap-1">
                    <span className="text-sm font-medium">Manufacturer</span>
                    <ValueSetAutocomplete
                      binding="http://terminology.hl7.org/ValueSet/v2-0227"
                      values={
                        value
                          ? [
                              {
                                code,
                                display,
                                system: 'http://hl7.org/fhir/sid/mvx',
                              },
                            ]
                          : []
                      }
                      onSelect={(value) => {
                        onChange(value ?? { code: '', display: '' })
                      }}
                      {...fieldRest}
                    >
                      <Button
                        variant="outline"
                        className="w-full justify-between"
                      >
                        {code ? `${code} - ${display}` : 'Search manufacturer…'}
                      </Button>
                    </ValueSetAutocomplete>
                  </label>
                )
              }}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="doseQuantity"
              render={({ field }) => (
                <label className="flex flex-col gap-1">
                  <span className="text-sm font-medium">Dose Quantity</span>
                  <input
                    {...field}
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value || null)}
                    className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                    placeholder="e.g., 0.5"
                    type="number"
                    step="0.1"
                  />
                </label>
              )}
            />

            <FormField
              control={form.control}
              name="doseUnit"
              render={({ field }) => (
                <label className="flex flex-col gap-1">
                  <span className="text-sm font-medium">Dose Unit</span>
                  <Combobox
                    onSelect={(value) => field.onChange(value)}
                    options={[
                      {
                        value: 'ml',
                        label: 'ml',
                        keywords: ['ml', 'milliliter'],
                      },
                      {
                        value: 'mg',
                        label: 'mg',
                        keywords: ['mg', 'milligram'],
                      },
                      {
                        value: 'mcg',
                        label: 'mcg',
                        keywords: ['mcg', 'microgram'],
                      },
                      {
                        value: 'units',
                        label: 'units',
                        keywords: ['units', 'iu'],
                      },
                    ]}
                  >
                    <Button
                      variant="outline"
                      className="w-full justify-between"
                    >
                      {field.value ?? 'e.g., ml'}
                    </Button>
                  </Combobox>
                </label>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="reason"
            render={({ field }) => (
              <label className="flex flex-col gap-1">
                <span className="text-sm font-medium">Reason</span>
                <input
                  {...field}
                  value={field.value ?? ''}
                  onChange={(e) => field.onChange(e.target.value || null)}
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                  placeholder="e.g., Routine immunization"
                />
              </label>
            )}
          />
        </>
      )}

      <FormField
        control={form.control}
        name="notes"
        render={({ field }) => (
          <label className="flex flex-col gap-1">
            <span className="text-sm font-medium">Notes</span>
            <textarea
              {...field}
              value={field.value ?? ''}
              onChange={(e) => field.onChange(e.target.value || null)}
              className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              placeholder="Additional notes..."
              rows={3}
            />
          </label>
        )}
      />

      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            form.reset()
            onCancel()
          }}
          disabled={createImmunization.isPending}
        >
          Cancel
        </Button>
        {hasVaccine ? (
          <Button
            type="submit"
            disabled={createImmunization.isPending || isReadOnly}
          >
            {createImmunization.isPending
              ? 'Creating...'
              : 'Create Immunization'}
          </Button>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              {/* oxlint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- a disabled button emits no pointer events, so the tooltip explaining why it is disabled needs a focusable wrapper to open at all */}
              <span tabIndex={0}>
                <Button type="submit" disabled>
                  Create Immunization
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              Select a vaccine to create the immunization.
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </Form>
  )
}
